(() => {
  if (customElements.get('trusted-community-shoppable-reels')) return;

  class TrustedCommunityReels extends HTMLElement {
    constructor() {
      super();
      this.abortController = null;
      this.resizeObserver = null;
      this.sectionObserver = null;
      this.reelObserver = null;
      this.activeTrigger = null;
      this.modalVideo = null;
      this.autoplayRetryTimers = [];
      this.sectionVisible = false;
      this.visibleReels = new Set();
      // Cubitt: reels que deben reproducirse ahora y proporción visible de cada uno.
      this.activePreviews = new Set();
      this.reelRatios = new Map();
      this.reviewIndex = 0;
      this.reviewTimer = null;
      this.reviewLeaveTimer = null;
    }

    connectedCallback() {
      if (this.dataset.initialized === 'true') return;

      this.dataset.initialized = 'true';
      this.dataset.componentVersion = '5';
      this.abortController = new AbortController();
      this.signal = this.abortController.signal;

      this.track = this.querySelector('[data-reels-track]');
      this.previousButton = this.querySelector('[data-reels-previous]');
      this.nextButton = this.querySelector('[data-reels-next]');
      this.dialog = this.querySelector('[data-reel-dialog]');
      this.dialogMedia = this.querySelector('[data-dialog-media]');
      this.dialogProduct = this.querySelector('[data-dialog-product]');
      this.dialogTitle = this.querySelector('[data-dialog-title]');
      this.closeButton = this.querySelector('[data-dialog-close]');
      this.reels = Array.from(this.querySelectorAll('[data-reel]'));
      this.showNavigation = this.dataset.showNavigation === 'true';

      this.reviewsRoot = this.querySelector('[data-reviews]');
      this.reviewSlides = Array.from(this.querySelectorAll('[data-review-slide]'));
      this.reviewPrevButton = this.querySelector('[data-reviews-previous]');
      this.reviewNextButton = this.querySelector('[data-reviews-next]');
      this.reviewCurrentLabel = this.querySelector('[data-reviews-current]');
      this.reviewAutoplay = this.dataset.reviewAutoplay === 'true';
      this.reviewInterval = Number.parseInt(this.dataset.reviewInterval, 10) || 5500;
      // Cubitt: en mobile solo se reproduce 1 reel a la vez; con reduced motion ninguno.
      this.mobileQuery = window.matchMedia('(max-width: 749px)');
      this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      // Cubitt: setting "Reproducción de los vídeos": "in_view" (al ver la sección) o "hover" (al pasar el cursor por
      // encima, solo en dispositivos con cursor; en táctil se usa "in_view").
      this.playTrigger = this.dataset.playTrigger === 'hover' && window.matchMedia('(hover: hover) and (pointer: fine)').matches ? 'hover' : 'in_view';
      this.sectionActive = false;
      this.isHovered = false;

      if (this.previousButton) {
        this.previousButton.removeAttribute('hidden');
        this.previousButton.classList.add('tc-nav--hidden');
      }
      if (this.nextButton) {
        this.nextButton.removeAttribute('hidden');
        this.nextButton.classList.add('tc-nav--hidden');
      }

      this.bindEvents();
      this.setupCarouselObserver();
      this.updateNavigation();
      this.setupSectionObserver();
      this.setupReelObserver();
      this.setupReviewsSlider();
    }

    disconnectedCallback() {
      this.playZoneObserver?.disconnect();
      this.pauseAllPreviews();
      this.destroyModalVideo();
      this.clearAutoplayRetries();
      this.stopReviewAutoplay();
      window.clearTimeout(this.reviewLeaveTimer);
      this.abortController?.abort();
      this.resizeObserver?.disconnect();
      this.sectionObserver?.disconnect();
      this.reelObserver?.disconnect();
      this.dataset.initialized = 'false';
    }

    getVideoForReel(reel) {
      return reel.querySelector('video');
    }

    setupSectionObserver() {
      // Cubitt: los vídeos solo se reproducen cuando la sección ocupa la zona central de la pantalla (no al asomar por
      // abajo) y se pausan en cuanto sale de ella; en modo "hover", además, solo con el cursor encima.
      this.playZoneObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            this.sectionActive = entry.isIntersecting;
            this.syncPreviews();
          });
        },
        { rootMargin: '-25% 0px -25% 0px', threshold: 0 }
      );
      this.playZoneObserver.observe(this);

      if (this.playTrigger === 'hover') {
        this.addEventListener('pointerenter', () => {
          this.isHovered = true;
          this.syncPreviews();
        }, { signal: this.signal });
        this.addEventListener('pointerleave', () => {
          this.isHovered = false;
          this.syncPreviews();
        }, { signal: this.signal });
      }

      this.sectionObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            this.sectionVisible = entry.isIntersecting;
            if (!entry.isIntersecting) {
              this.pauseAllPreviews();
              this.stopReviewAutoplay();
            } else {
              this.startReviewAutoplay();
            }
          });
        },
        { threshold: 0.05 }
      );
      this.sectionObserver.observe(this);
    }

    setupReelObserver() {
      this.reelObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const reel = entry.target;
            this.reelRatios.set(reel, entry.intersectionRatio);

            // Cubitt: visible = al menos 30 % del reel en pantalla (antes nunca salía hasta 0 %).
            if (entry.isIntersecting && entry.intersectionRatio >= 0.3) {
              this.visibleReels.add(reel);
            } else {
              this.visibleReels.delete(reel);
            }
          });

          this.syncPreviews();
        },
        { threshold: [0, 0.3, 0.6, 0.9] }
      );

      this.reels.forEach((reel) => this.reelObserver.observe(reel));
    }

    // Cubitt: respeta el setting de block "autoplay" y prefers-reduced-motion.
    canAutoplay(reel) {
      return reel.dataset.autoplay !== 'false' && !this.reducedMotionQuery.matches;
    }

    // Cubitt: reproduce solo los reels visibles con autoplay (1 en mobile, el más visible) y pausa el resto.
    // Con preload="none" un reel no descarga nada hasta que entra aquí.
    syncPreviews() {
      if (document.hidden || this.isDialogOpen()) return;

      if (!this.sectionActive || (this.playTrigger === 'hover' && !this.isHovered)) {
        this.pauseAllPreviews();
        return;
      }

      let targets = Array.from(this.visibleReels).filter(
        (reel) => this.canAutoplay(reel) && this.getVideoForReel(reel)
      );

      if (this.mobileQuery.matches && targets.length > 1) {
        targets = [
          targets.reduce((best, reel) =>
            (this.reelRatios.get(reel) || 0) > (this.reelRatios.get(best) || 0) ? reel : best
          )
        ];
      }

      this.activePreviews = new Set(targets);

      this.reels.forEach((reel) => {
        const video = this.getVideoForReel(reel);
        if (!video) return;

        if (this.activePreviews.has(reel)) {
          this.initAndPlayVideo(video);
        } else if (!video.paused) {
          video.pause();
        }
      });
    }

    /* ======================================================================
       REVIEWS SLIDER
       ====================================================================== */

    setupReviewsSlider() {
      if (!this.reviewsRoot || this.reviewSlides.length <= 1) return;

      this.reviewPrevButton?.addEventListener('click', () => {
        this.goToReview(this.reviewIndex - 1, true);
      }, { signal: this.signal });

      this.reviewNextButton?.addEventListener('click', () => {
        this.goToReview(this.reviewIndex + 1, true);
      }, { signal: this.signal });

      this.reviewsRoot.addEventListener('mouseenter', () => this.stopReviewAutoplay(), { signal: this.signal });
      this.reviewsRoot.addEventListener('mouseleave', () => this.startReviewAutoplay(), { signal: this.signal });
      this.reviewsRoot.addEventListener('focusin', () => this.stopReviewAutoplay(), { signal: this.signal });
      this.reviewsRoot.addEventListener('focusout', () => this.startReviewAutoplay(), { signal: this.signal });

      this.updateReviewCounter();
      this.startReviewAutoplay();
    }

    updateReviewCounter() {
      if (this.reviewCurrentLabel) {
        this.reviewCurrentLabel.textContent = String(this.reviewIndex + 1);
      }
    }

    goToReview(nextIndex, userInitiated) {
      if (!this.reviewSlides.length) return;

      const total = this.reviewSlides.length;
      const target = ((nextIndex % total) + total) % total;
      if (target === this.reviewIndex) {
        if (userInitiated) this.startReviewAutoplay();
        return;
      }

      const current = this.reviewSlides[this.reviewIndex];
      const next = this.reviewSlides[target];

      window.clearTimeout(this.reviewLeaveTimer);

      if (current) {
        current.classList.remove('is-active');
        current.classList.add('is-leaving');
        current.setAttribute('aria-hidden', 'true');
        this.reviewLeaveTimer = window.setTimeout(() => {
          current.classList.remove('is-leaving');
        }, 640);
      }

      next.classList.remove('is-leaving');
      next.classList.add('is-active');
      next.removeAttribute('aria-hidden');

      this.reviewIndex = target;
      this.updateReviewCounter();

      if (userInitiated) this.startReviewAutoplay();
    }

    startReviewAutoplay() {
      this.stopReviewAutoplay();
      if (!this.reviewAutoplay || this.reviewSlides.length <= 1) return;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

      this.reviewTimer = window.setInterval(() => {
        if (document.hidden) return;
        this.goToReview(this.reviewIndex + 1, false);
      }, this.reviewInterval);
    }

    stopReviewAutoplay() {
      if (this.reviewTimer) {
        window.clearInterval(this.reviewTimer);
        this.reviewTimer = null;
      }
    }

    initAndPlayVideo(video) {
      if (video.dataset.initialized === 'true') {
        this.playPreview(video);
        return;
      }

      video.dataset.initialized = 'true';
      video.autoplay = false;
      // Cubitt: el bucle lo decide el setting de block "loop" (atributo loop del video_tag); antes se forzaba.
      video.muted = true;
      video.defaultMuted = true;
      video.playsInline = true;
      video.controls = false;
      video.preload = 'metadata';

      video.setAttribute('muted', '');
      video.setAttribute('playsinline', '');
      video.setAttribute('webkit-playsinline', '');
      video.setAttribute('preload', 'metadata');

      video.load();

      const onReady = () => this.playPreview(video);
      video.addEventListener('canplay', onReady, { once: true, signal: this.signal });
      video.addEventListener('loadedmetadata', onReady, { once: true, signal: this.signal });

      video.addEventListener('pause', () => {
        const reel = video.closest('[data-reel]');
        // Cubitt: no relanzar si terminó (loop desactivado) o si ya no es el reel activo.
        if (reel && !video.ended && this.activePreviews.has(reel) && !document.hidden && !this.isDialogOpen()) {
          window.setTimeout(() => this.playPreview(video), 80);
        }
      }, { signal: this.signal });
    }

    bindEvents() {
      this.querySelectorAll('[data-reel-trigger]').forEach((trigger) => {
        trigger.addEventListener('click', () => this.openDialog(trigger), { signal: this.signal });
      });

      this.closeButton?.addEventListener('click', () => this.closeDialog(), { signal: this.signal });

      this.dialog?.addEventListener('click', (event) => {
        if (event.target === this.dialog) this.closeDialog();
      }, { signal: this.signal });

      this.dialog?.addEventListener('close', () => this.handleDialogClosed(), { signal: this.signal });
      this.dialog?.addEventListener('cancel', (event) => {
        event.preventDefault();
        this.closeDialog();
      }, { signal: this.signal });

      this.previousButton?.addEventListener('click', () => this.scrollTrack(-1), { signal: this.signal });
      this.nextButton?.addEventListener('click', () => this.scrollTrack(1), { signal: this.signal });
      this.track?.addEventListener('scroll', () => this.updateNavigation(), { passive: true, signal: this.signal });

      document.addEventListener('visibilitychange', () => {
        if (document.hidden) {
          this.pauseAllPreviews();
          this.stopReviewAutoplay();
        } else {
          this.playVisiblePreviews();
          if (this.sectionVisible) this.startReviewAutoplay();
        }
      }, { signal: this.signal });

      window.addEventListener('pageshow', () => this.playVisiblePreviews(), { signal: this.signal });
      window.addEventListener('focus', () => this.playVisiblePreviews(), { signal: this.signal });
      this.mobileQuery.addEventListener('change', () => this.syncPreviews(), { signal: this.signal });
      this.reducedMotionQuery.addEventListener('change', () => this.syncPreviews(), { signal: this.signal });
    }

    setupCarouselObserver() {
      if (!this.track || !('ResizeObserver' in window)) return;

      this.resizeObserver = new ResizeObserver(() => this.updateNavigation());
      this.resizeObserver.observe(this.track);
    }

    playPreview(video) {
      if (!video || document.hidden || this.isDialogOpen()) return;
      // Cubitt: puede llegar "canplay" cuando el reel ya dejó de ser el activo.
      if (!this.activePreviews.has(video.closest('[data-reel]'))) return;

      video.muted = true;
      video.defaultMuted = true;

      const playPromise = video.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => {});
      }
    }

    playVisiblePreviews() {
      this.syncPreviews();
    }

    pauseAllPreviews() {
      this.activePreviews.clear();
      this.reels.forEach((reel) => {
        const video = this.getVideoForReel(reel);
        if (video) video.pause();
      });
    }

    scheduleAutoplayRetries() {
      this.clearAutoplayRetries();
      [100, 400, 900].forEach((delay) => {
        const timer = window.setTimeout(() => this.playVisiblePreviews(), delay);
        this.autoplayRetryTimers.push(timer);
      });
    }

    clearAutoplayRetries() {
      this.autoplayRetryTimers.forEach((timer) => window.clearTimeout(timer));
      this.autoplayRetryTimers = [];
    }

    isDialogOpen() {
      return Boolean(this.dialog?.open || this.dialog?.hasAttribute('open'));
    }

    openDialog(trigger) {
      const previewVideo = trigger.querySelector('video');
      if (!previewVideo || !this.dialog || !this.dialogMedia) return;

      this.clearAutoplayRetries();
      this.pauseAllPreviews();
      this.stopReviewAutoplay();
      this.destroyModalVideo();
      this.activeTrigger = trigger;

      const modalVideo = previewVideo.cloneNode(true);
      modalVideo.autoplay = false;
      modalVideo.loop = false;
      modalVideo.controls = true;
      modalVideo.muted = false;
      modalVideo.defaultMuted = false;
      modalVideo.playsInline = true;
      modalVideo.preload = 'auto';
      modalVideo.removeAttribute('autoplay');
      modalVideo.removeAttribute('loop');
      modalVideo.removeAttribute('muted');
      modalVideo.setAttribute('controls', '');
      modalVideo.setAttribute('controlslist', 'nodownload noremoteplayback');
      modalVideo.setAttribute('playsinline', '');
      modalVideo.setAttribute('webkit-playsinline', '');
      modalVideo.disablePictureInPicture = false;
      modalVideo.className = 'trusted-community__modal-video';

      this.dialogMedia.appendChild(modalVideo);
      this.modalVideo = modalVideo;

      const reel = trigger.closest('[data-reel]');
      const productTemplate = reel?.querySelector('template[data-reel-product-template]');
      const hasAssignedProduct =
        reel?.dataset.hasProduct === 'true' &&
        productTemplate instanceof HTMLTemplateElement &&
        productTemplate.content.childElementCount > 0;

      if (this.dialogProduct) {
        this.dialogProduct.replaceChildren();

        if (hasAssignedProduct) {
          const productContent = productTemplate.content.cloneNode(true);
          this.dialogProduct.appendChild(productContent);
        }
      }

      this.dialog.classList.toggle(
        'trusted-community__dialog--has-product',
        Boolean(hasAssignedProduct)
      );

      if (this.dialogTitle) {
        this.dialogTitle.textContent = trigger.getAttribute('aria-label') || 'Community video';
      }

      if (typeof this.dialog.showModal === 'function') {
        this.dialog.showModal();
      } else {
        this.dialog.setAttribute('open', '');
      }

      const syncCurrentTime = () => {
        try {
          if (Number.isFinite(previewVideo.currentTime)) {
            modalVideo.currentTime = previewVideo.currentTime;
          }
        } catch (error) {}
      };

      if (modalVideo.readyState >= 1) {
        syncCurrentTime();
      } else {
        modalVideo.addEventListener('loadedmetadata', syncCurrentTime, { once: true });
      }

      requestAnimationFrame(() => {
        modalVideo.volume = 1;
        const playPromise = modalVideo.play();
        if (playPromise && typeof playPromise.catch === 'function') {
          playPromise.catch(() => {});
        }
        this.closeButton?.focus({ preventScroll: true });
      });
    }

    closeDialog() {
      if (!this.dialog) return;

      this.modalVideo?.pause();

      if (typeof this.dialog.close === 'function' && this.dialog.open) {
        this.dialog.close();
      } else {
        this.dialog.removeAttribute('open');
        this.handleDialogClosed();
      }
    }

    handleDialogClosed() {
      this.destroyModalVideo();
      this.activeTrigger?.focus({ preventScroll: true });
      this.activeTrigger = null;
      this.playVisiblePreviews();
      this.scheduleAutoplayRetries();
      if (this.sectionVisible) this.startReviewAutoplay();
    }

    destroyModalVideo() {
      if (this.modalVideo) {
        this.modalVideo.pause();
        this.modalVideo.removeAttribute('src');
        this.modalVideo.querySelectorAll('source').forEach((source) => source.removeAttribute('src'));
        this.modalVideo.load();
        this.modalVideo.remove();
      }

      this.modalVideo = null;

      if (this.dialogMedia) {
        this.dialogMedia.replaceChildren();
      }

      if (this.dialogProduct) {
        this.dialogProduct.replaceChildren();
      }

      this.dialog?.classList.remove('trusted-community__dialog--has-product');
    }

    scrollTrack(direction) {
      if (!this.track) return;

      const firstReel = this.track.querySelector('[data-reel]');
      if (!firstReel) return;

      const styles = window.getComputedStyle(this.track);
      const gap = Number.parseFloat(styles.columnGap || styles.gap || '0') || 0;
      const distance = firstReel.getBoundingClientRect().width + gap;

      this.track.scrollBy({
        left: direction * distance,
        behavior: 'smooth'
      });
    }

    updateNavigation() {
      if (!this.track || !this.previousButton || !this.nextButton) return;

      const hasOverflow = this.track.scrollWidth > this.track.clientWidth + 2;
      const showControls = this.showNavigation && hasOverflow;
      const maxScroll = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      const currentScroll = Math.max(0, this.track.scrollLeft);

      const prevHidden = !showControls || currentScroll <= 2;
      const nextHidden = !showControls || currentScroll >= maxScroll - 2;

      this.previousButton.classList.toggle('tc-nav--hidden', prevHidden);
      this.nextButton.classList.toggle('tc-nav--hidden', nextHidden);

      this.previousButton.disabled = prevHidden;
      this.nextButton.disabled = nextHidden;
    }
  }

  customElements.define('trusted-community-shoppable-reels', TrustedCommunityReels);
})();
